import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import {
  LayoutDashboard, Users, Briefcase, ListChecks, MailWarning, Send, BarChart3,
  FileText, Settings as SettingsIcon, ShieldCheck, LogOut, ScrollText, ClipboardList,
  KeyRound, Receipt, UsersRound,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/jobs", label: "Jobs & deadlines", icon: Briefcase, testid: "nav-jobs" },
  { to: "/services", label: "Service templates", icon: ClipboardList, testid: "nav-services" },
  { to: "/reminders", label: "Reminder queue", icon: MailWarning, testid: "nav-reminders" },
  { to: "/chasers", label: "Client chasers", icon: Send, testid: "nav-chasers" },
  { to: "/email-templates", label: "Email templates", icon: FileText, testid: "nav-email-templates" },
  { to: "/email-logs", label: "Email logs", icon: ScrollText, testid: "nav-email-logs" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/invoices", label: "Invoices", icon: Receipt, testid: "nav-invoices" },
  { to: "/vault", label: "Personal vault", icon: KeyRound, testid: "nav-vault" },
  { to: "/users", label: "User management", icon: UsersRound, testid: "nav-users", adminOnly: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function Layout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  if (loading || !user) {
    return <div className="min-h-screen grid place-items-center text-slate-500 text-sm">Loading…</div>;
  }

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside data-testid="sidebar" className="w-64 shrink-0 border-r border-slate-200 bg-white flex flex-col">
        <div className="h-16 px-5 flex items-center gap-3 border-b border-slate-200">
          <img
            src="https://customer-assets-4nw71qhi.emergentagent.net/job_taxegy-practice/artifacts/b8tuv913_Taxegy%20PM%20Logo.png"
            alt="Taxegy Practice Manager"
            className="h-8 w-8 rounded-md object-contain"
            data-testid="sidebar-logo"
          />
          <div>
            <div className="font-heading text-sm font-semibold tracking-tight text-slate-900">Taxegy</div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Practice Manager</div>
          </div>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1">
          {NAV.filter((n) => !n.adminOnly || user.role === "admin").map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
                  isActive
                    ? "bg-slate-900 text-white"
                    : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <div className="flex items-center gap-3 px-2 py-2">
            {user.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-slate-200 grid place-items-center text-xs">
                {user.name?.[0]?.toUpperCase() || "U"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div data-testid="user-name" className="text-sm font-medium text-slate-900 truncate">{user.name}</div>
              <div className="text-[11px] text-slate-500 truncate">{user.email}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="text-slate-500 hover:text-slate-900 p-1 rounded transition-colors"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold px-2">
            Role: <span className="text-slate-800">{user.role}</span>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <div className="flex-1 min-w-0">
          <Outlet />
        </div>
        <Toaster />
      </main>
    </div>
  );
}
